import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";

export default function Terms() {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 pt-32 pb-20">
        <h1 className="text-4xl lg:text-5xl font-bold text-vokaris-teal mb-8">
          Terms of Service
        </h1>

        <div className="prose prose-lg max-w-none">
          <p className="text-lg text-vokaris-teal/70 mb-6">
            <strong>Last updated:</strong> December 1, 2025
          </p>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-vokaris-teal mb-4">
              1. Acceptance of Terms
            </h2>
            <p className="text-vokaris-teal/70 mb-4">
              By accessing or using Vokaris services, you agree to be bound by
              these Terms of Service ("Terms"). If you do not agree to these
              Terms, please do not use our services.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-vokaris-teal mb-4">
              2. Description of Service
            </h2>
            <p className="text-vokaris-teal/70 mb-4">
              Vokaris provides a comprehensive infrastructure platform that
              includes:
            </p>
            <ul className="list-disc list-inside text-vokaris-teal/70 mb-4 space-y-2">
              <li>Identity and access management</li>
              <li>
                Compute resources (bare-metal, virtual machines, serverless)
              </li>
              <li>Storage and secrets management</li>
              <li>Billing and subscription management</li>
              <li>Notifications and app services</li>
              <li>SDKs and APIs for integration</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-vokaris-teal mb-4">
              3. Account Registration
            </h2>
            <p className="text-vokaris-teal/70 mb-4">
              To use our services, you must:
            </p>
            <ul className="list-disc list-inside text-vokaris-teal/70 mb-4 space-y-2">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Be at least 18 years old or have parental consent</li>
              <li>
                Represent a legally registered business entity (for business
                accounts)
              </li>
              <li>Notify us immediately of any unauthorized access</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-vokaris-teal mb-4">
              4. Acceptable Use Policy
            </h2>
            <p className="text-vokaris-teal/70 mb-4">
              You agree NOT to use our services to:
            </p>
            <ul className="list-disc list-inside text-vokaris-teal/70 mb-4 space-y-2">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe upon intellectual property rights</li>
              <li>Transmit malware, viruses, or malicious code</li>
              <li>Engage in cryptocurrency mining without authorization</li>
              <li>Conduct denial-of-service attacks or network disruptions</li>
              <li>Attempt unauthorized access to other accounts or systems</li>
              <li>Store or distribute illegal content</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-vokaris-teal mb-4">
              5. Billing and Payment
            </h2>
            <h3 className="text-xl font-semibold text-vokaris-teal mb-3">
              5.1 Fees
            </h3>
            <p className="text-vokaris-teal/70 mb-4">
              You agree to pay all fees associated with your account. Fees are
              based on your selected plan and resource usage. Current pricing is
              available on our website.
            </p>

            <h3 className="text-xl font-semibold text-vokaris-teal mb-3">
              5.2 Payment Terms
            </h3>
            <ul className="list-disc list-inside text-vokaris-teal/70 mb-4 space-y-2">
              <li>Payment is due at the beginning of each billing cycle</li>
              <li>
                We accept credit cards, ACH, and wire transfers (for enterprise
                accounts)
              </li>
              <li>Late payments may result in service suspension</li>
              <li>All fees are non-refundable unless otherwise stated</li>
            </ul>

            <h3 className="text-xl font-semibold text-vokaris-teal mb-3">
              5.3 Usage Limits
            </h3>
            <p className="text-vokaris-teal/70 mb-4">
              Usage beyond your plan limits may result in additional charges. We
              will notify you before applying overage fees.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-vokaris-teal mb-4">
              6. Data and Content
            </h2>
            <h3 className="text-xl font-semibold text-vokaris-teal mb-3">
              6.1 Your Data
            </h3>
            <p className="text-vokaris-teal/70 mb-4">
              You retain all rights to your data. You grant us a limited license
              to store, process, and transmit your data solely to provide our
              services.
            </p>

            <h3 className="text-xl font-semibold text-vokaris-teal mb-3">
              6.2 Data Responsibility
            </h3>
            <p className="text-vokaris-teal/70 mb-4">
              You are responsible for:
            </p>
            <ul className="list-disc list-inside text-vokaris-teal/70 mb-4 space-y-2">
              <li>Maintaining backups of your data</li>
              <li>Ensuring your data complies with applicable laws</li>
              <li>
                Obtaining necessary rights and permissions for uploaded content
              </li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-vokaris-teal mb-4">
              7. Service Level Agreement
            </h2>
            <p className="text-vokaris-teal/70 mb-4">
              We strive to maintain 99.9% uptime for our core services. Service
              level commitments vary by plan and are detailed in our SLA
              documentation. Scheduled maintenance windows are excluded from
              uptime calculations.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-vokaris-teal mb-4">
              8. Intellectual Property
            </h2>
            <p className="text-vokaris-teal/70 mb-4">
              Vokaris retains all rights, title, and interest in our platform,
              services, documentation, and related intellectual property. You
              may not:
            </p>
            <ul className="list-disc list-inside text-vokaris-teal/70 mb-4 space-y-2">
              <li>Copy, modify, or reverse engineer our services</li>
              <li>Use our trademarks without written permission</li>
              <li>Create derivative works based on our platform</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-vokaris-teal mb-4">
              9. Limitation of Liability
            </h2>
            <p className="text-vokaris-teal/70 mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, VOKARIS SHALL NOT BE
              LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
              PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR BUSINESS
              OPPORTUNITIES.
            </p>
            <p className="text-vokaris-teal/70 mb-4">
              Our total liability shall not exceed the amount you paid us in the
              12 months preceding the claim.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-vokaris-teal mb-4">
              10. Indemnification
            </h2>
            <p className="text-vokaris-teal/70 mb-4">
              You agree to indemnify and hold Vokaris harmless from any claims,
              damages, losses, and expenses arising from your use of our
              services or violation of these Terms.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-vokaris-teal mb-4">
              11. Termination
            </h2>
            <p className="text-vokaris-teal/70 mb-4">
              Either party may terminate this agreement:
            </p>
            <ul className="list-disc list-inside text-vokaris-teal/70 mb-4 space-y-2">
              <li>For convenience with 30 days' notice</li>
              <li>Immediately for breach of these Terms</li>
              <li>Immediately if required by law</li>
            </ul>
            <p className="text-vokaris-teal/70 mb-4">
              Upon termination, you must cease all use of our services and we
              will delete your data within 30 days, subject to legal retention
              requirements.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-vokaris-teal mb-4">
              12. Modifications to Terms
            </h2>
            <p className="text-vokaris-teal/70 mb-4">
              We reserve the right to modify these Terms at any time. Material
              changes will be communicated via email and posted on our website.
              Continued use of our services after changes constitute acceptance
              of the new Terms.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-vokaris-teal mb-4">
              13. Governing Law
            </h2>
            <p className="text-vokaris-teal/70 mb-4">
              These Terms shall be governed by the laws of [Your Jurisdiction].
              Any disputes shall be resolved through binding arbitration in the
              United States of America.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-vokaris-teal mb-4">
              14. Contact Information
            </h2>
            <p className="text-vokaris-teal/70 mb-4">
              For questions about these Terms, please contact:
            </p>
            <p className="text-vokaris-teal/70">
              Email:{" "}
              <a
                href="mailto:hello@vokaris.io"
                className="text-vokaris-green font-semibold hover:underline"
              >
                hello@vokaris.io
              </a>
              <br />
              Address: Vokaris Corporation, 1309 Coffeen Avenue STE 1200,
              Sheridan, WY 82801, USA
            </p>
          </section>
        </div>

        {/* Back Link */}
        <div className="mt-12">
          <Link
            to="/"
            className="text-vokaris-green font-semibold hover:text-vokaris-teal transition"
          >
            ← Back to Home
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
